// Figma design: https://www.figma.com/design/sseCNpJYq9VS8RO8p4u6HJ/Ticket-Purchase-Flow?node-id=18124-61748

import { Box, Card, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { EventTicketsAccordion, useIsMobile } from '@tix-ticketing/ui-components';
import type { TicketData } from '@tix-ticketing/ui-components';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { getApiEventsByEventIdUpcomingOptions } from 'api/generated/@tanstack/react-query.gen';
import type { ExchangeReturnedTicketDto } from 'api/generated/types.gen';
import { CartAsideEventCardBody } from 'components/common/cards/cart-aside-event-card-body';
import { EventCardBody } from 'components/common/cards/event-card-body';
import { useAppState } from 'lib/app-state';
import {
  getReturnedTicketsTotal,
  groupReturnedTicketsByEvent,
} from 'lib/helpers/exchange-helpers';
import { useCurrencyValue } from 'lib/helpers/formatted-price';

/** The exchanged-away tickets, shown as a disabled copy of the cart's event
 *  card so the customer can see what they are giving up. */
export function ExchangeReturnedTickets({
  tickets,
  inAside,
  sx,
}: {
  tickets: ExchangeReturnedTicketDto[];
  /** Mini-cart variant: the narrower card the cart's aside uses. */
  inAside?: boolean;
  /** Spacing for the section, so a page can align it with its own headings. */
  sx?: SxProps<Theme>;
}) {
  const { tempExchangeData } = useAppState();

  // An order can be exchanged more than once, each time handing back tickets to
  // a different event, so the tickets get a card per event rather than all
  // hanging under the first one.
  const groups = useMemo(() => groupReturnedTicketsByEvent(tickets), [tickets]);

  // What the exchange in progress already knows about the event it is taking
  // tickets from. It describes one event, so it only stands in while there is
  // one group to describe — which is every case in the cart. A receipt of
  // several exchanges leaves each group to its own lookup.
  const fallback =
    groups.length === 1
      ? {
          eventId: tempExchangeData?.sourceEventId,
          eventName: tempExchangeData?.sourceEventName,
        }
      : undefined;

  return (
    <Stack
      spacing={inAside ? 1.5 : 2}
      data-testid="exchange-returned-tickets"
      sx={[inAside && { pt: 2 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Typography variant={inAside ? 'body1Bold' : 'h6'} component="h2">
        <FormattedMessage
          id="exchangeCart.returnedTickets"
          defaultMessage="Returned tickets"
        />
      </Typography>
      {groups.map((group) => (
        <ReturnedTicketsEventCard
          key={group.eventId ?? 'unknown'}
          eventId={group.eventId ?? fallback?.eventId}
          fallbackEventName={fallback?.eventName}
          tickets={group.tickets}
          inAside={inAside}
        />
      ))}
    </Stack>
  );
}

/** The tickets one exchange handed back, under the event they came from. */
function ReturnedTicketsEventCard({
  eventId,
  fallbackEventName,
  tickets,
  inAside,
}: {
  eventId: number | undefined;
  fallbackEventName: string | undefined;
  tickets: ExchangeReturnedTicketDto[];
  inAside?: boolean;
}) {
  const isMobile = useIsMobile();
  const { currency } = useAppState();
  const currencyValue = useCurrencyValue();

  // The customer holds tickets to this event, so it is read from the same
  // upcoming-event payload as their account page rather than the on-sale event
  // endpoint, whose sale may already have closed.
  const eventQuery = useQuery({
    ...getApiEventsByEventIdUpcomingOptions({
      path: { eventId: eventId ?? 0 },
    }),
    enabled: !!eventId,
    // Nothing here depends on the lookup succeeding, so failures settle at once
    // instead of holding the card through a retry backoff.
    retry: false,
  });
  const event = eventQuery.data?.event;

  // The event lookup only enriches the card; the exchange itself already knows
  // which event the tickets came from, so its name stands in when the lookup is
  // disabled or fails — an empty name would leave the card on its skeleton.
  const eventName = event?.name ?? fallbackEventName ?? '';

  // Returned tickets carry row and seat names even on unnumbered events, so
  // the event decides whether they mean anything. Without a lookup the names
  // are all there is to go on.
  const eventIsNumbered = event?.isNumbered;

  const ticketData = useMemo<TicketData[]>(
    () =>
      tickets.map((ticket, index) => ({
        encryptedId: `returned-${eventId ?? 'unknown'}-${index}`,
        ticketTypeName: ticket.ticketTypeName,
        priceZoneName: ticket.priceZoneName,
        isNumbered:
          (eventIsNumbered ?? true) && !!(ticket.rowName || ticket.seatName),
        row: ticket.rowName,
        seat: ticket.seatName,
        priceWithFeesAndTax: ticket.priceWithTax,
      })),
    [tickets, eventId, eventIsNumbered]
  );

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: inAside ? 2 : 3,
        border: 'none',
        backgroundColor: 'action.disabledBackground',
      }}
    >
      {/* The event labels set their own colours, so the inert card hands
          every one of them the disabled colour. */}
      <Box
        sx={{
          color: 'text.disabled',
          '& .MuiTypography-root': { color: 'inherit' },
        }}
      >
        {inAside ? (
          <CartAsideEventCardBody
            eventName={eventName}
            eventSubtitle={event?.subTitle ?? ''}
            eventStartsAt={event?.startDate ?? ''}
            eventImageUrl={event?.eventGroupFeatureImage ?? ''}
            hallName={
              eventQuery.data?.venue?.name ?? event?.hallName ?? undefined
            }
            eventTotal={getReturnedTicketsTotal(tickets)}
            // The skeleton is only for having nothing to show yet: once a
            // name is known the card renders, and the lookup fills in the
            // rest as it arrives.
            loading={eventQuery.isLoading && !eventName}
          />
        ) : (
          <EventCardBody
            eventId={eventId ?? 0}
            eventName={eventName}
            eventSubtitle={event?.subTitle ?? ''}
            eventStartsAt={event?.startDate ?? ''}
            eventImageUrl={event?.eventGroupFeatureImage ?? ''}
            hallName={
              eventQuery.data?.venue?.name ?? event?.hallName ?? undefined
            }
            eventTotal={getReturnedTicketsTotal(tickets)}
            imageSize={isMobile ? 64 : 80}
            loading={eventQuery.isLoading && !eventName}
          />
        )}
      </Box>
      <EventTicketsAccordion
        disabled
        inAside={inAside}
        tickets={ticketData}
        currencyData={{ currencyName: currency, currencyValue }}
      />
    </Card>
  );
}
